import {
  type BinaryFilter,
  type Filter,
  flattenFilters,
  type UnaryFilter,
} from "@cubejs-client/core";
import type { z } from "zod/v4";
import { err, isErr, ok, type Result } from "../../../util/result";
import type {
  BOUND_OPERATORS,
  DateOperator,
  TEXT_OPERATORS,
  VideoLengthOperator,
} from "../constants";
import type { CubeMember, CubeView } from "./cube-meta";
import { cubeFilterSchema, leafMemberOf } from "./cube-query";

type Leaf = BinaryFilter | UnaryFilter;

type TextOperator = (typeof TEXT_OPERATORS)[number];
export type BoundOperator = (typeof BOUND_OPERATORS)[number];

export type Test =
  | { kind: "tag-presence"; column: string; operator: "set" | "notSet" }
  | {
      kind: "tag-values";
      column: string;
      operator: "equals" | "notEquals";
      values: string[];
    }
  | { kind: "presence"; field: string; operator: "set" | "notSet" }
  | {
      kind: "values";
      field: string;
      operator: "equals" | "notEquals";
      literals: string[];
      role: "measure" | "dimension";
    }
  | { kind: "text"; field: string; operator: TextOperator; literals: string[] }
  | { kind: "bound"; field: string; operator: BoundOperator; literal: string }
  | { kind: "date"; field: string; operator: DateOperator; values: string[] }
  | {
      kind: "video-lengths";
      dimension: string;
      bounds: readonly [Bound, ...Bound[]];
    };

export type Bound = { operator: VideoLengthOperator; literal: string };

export type TypedFilter =
  | { kind: "and"; children: TypedFilter[] }
  | { kind: "or"; children: TypedFilter[] }
  | Test;

export type FilterRole = "where" | "having";
export type RoledFilter = { role: FilterRole; typed: TypedFilter };

// Malloy's short string lexer rejects a raw newline; its unescape reads \uXXXX and passes any other escaped character through.
export const malloyString = (value: string) =>
  `'${value.replace(/[\\']|\p{Cc}/gu, (character) =>
    character === "\\" || character === "'"
      ? `\\${character}`
      : `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`,
  )}'`;

// The Cube parse has already checked each value against its member's type.
const LITERALS: Record<CubeMember["type"], (value: string) => string> = {
  string: malloyString,
  number: (value) => String(Number(value)),
  time: (date) => `@${date.slice(0, 10)}`,
};

const nameInModelOf = ({ member, cube }: { member: string; cube: CubeView }) =>
  member.slice(cube.name.length + 1);

export const fieldOf = ({ member, cube }: { member: string; cube: CubeView }) =>
  `\`${nameInModelOf({ member, cube })}\``;

const tagTestOf = ({
  leaf,
  member,
  column,
}: {
  leaf: Leaf;
  member: CubeMember;
  column: string;
}): Test => {
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return { kind: "tag-presence", column, operator: leaf.operator };
    }
    case "equals":
    case "notEquals": {
      return {
        kind: "tag-values",
        column,
        operator: leaf.operator,
        values: leaf.values,
      };
    }
    case "contains":
    case "notContains":
    case "startsWith":
    case "notStartsWith":
    case "endsWith":
    case "notEndsWith":
    case "gt":
    case "gte":
    case "lt":
    case "lte":
    case "inDateRange":
    case "notInDateRange":
    case "beforeDate":
    case "beforeOrOnDate":
    case "afterDate":
    case "afterOrOnDate": {
      throw new Error(
        `The query schema offers ${member.name} no ${leaf.operator}`,
      );
    }
    default: {
      return leaf satisfies never;
    }
  }
};

const firstOf = (values: readonly string[]): string => {
  const [first] = values;
  if (first === undefined) {
    throw new Error("The query schema keeps a value on every binary filter");
  }
  return first;
};

const valueTestOf = ({
  leaf,
  member,
  field,
  role,
}: {
  leaf: Leaf;
  member: CubeMember;
  field: string;
  role: "measure" | "dimension";
}): Test => {
  const literal = LITERALS[member.type];
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return { kind: "presence", field, operator: leaf.operator };
    }
    case "equals":
    case "notEquals": {
      const { operator, values } = leaf;
      return {
        kind: "values",
        field,
        operator,
        literals: values.map(literal),
        role,
      };
    }
    case "contains":
    case "notContains":
    case "startsWith":
    case "notStartsWith":
    case "endsWith":
    case "notEndsWith": {
      return {
        kind: "text",
        field: member.type === "string" ? field : `${field}::string`,
        operator: leaf.operator,
        literals: leaf.values.map(malloyString),
      };
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      return {
        kind: "bound",
        field,
        operator: leaf.operator,
        literal: literal(firstOf(leaf.values)),
      };
    }
    case "inDateRange":
    case "notInDateRange":
    case "afterDate":
    case "afterOrOnDate":
    case "beforeDate":
    case "beforeOrOnDate": {
      return {
        kind: "date",
        field,
        operator: leaf.operator,
        values: leaf.values,
      };
    }
    default: {
      return leaf satisfies never;
    }
  }
};

const videoLengthBoundOf = ({
  leaf,
  member,
}: {
  leaf: Leaf;
  member: CubeMember;
}): Bound => {
  switch (leaf.operator) {
    case "gte":
    case "lte": {
      return {
        operator: leaf.operator,
        literal: LITERALS.number(firstOf(leaf.values)),
      };
    }
    case "gt":
    case "lt":
    case "set":
    case "notSet":
    case "equals":
    case "notEquals":
    case "contains":
    case "notContains":
    case "startsWith":
    case "notStartsWith":
    case "endsWith":
    case "notEndsWith":
    case "inDateRange":
    case "notInDateRange":
    case "beforeDate":
    case "beforeOrOnDate":
    case "afterDate":
    case "afterOrOnDate": {
      throw new Error(
        `The query schema offers ${member.name} no ${leaf.operator}`,
      );
    }
    default: {
      return leaf satisfies never;
    }
  }
};

type VideoLengthBound = { dimension: string; bound: Bound };

// Bounds on one video length must hold for the same video, so they stay unjoined until the AND they belong to closes.
type Conjunction = {
  filters: readonly TypedFilter[];
  bounds: readonly VideoLengthBound[];
};

const conjunctionOfAll = (
  conjunctions: readonly Conjunction[],
): Conjunction => ({
  filters: conjunctions.flatMap(({ filters }) => filters),
  bounds: conjunctions.flatMap(({ bounds }) => bounds),
});

const videoLengthTestsOf = (
  bounds: readonly VideoLengthBound[],
): TypedFilter[] => {
  const byDimension = bounds.reduce<
    ReadonlyMap<string, readonly [Bound, ...Bound[]]>
  >((grouped, { dimension, bound }) => {
    const previous = grouped.get(dimension);
    return new Map(grouped).set(
      dimension,
      previous === undefined ? [bound] : [...previous, bound],
    );
  }, new Map());
  return [...byDimension].map(
    ([dimension, joined]): TypedFilter => ({
      kind: "video-lengths",
      dimension,
      bounds: joined,
    }),
  );
};

const closedFiltersOf = ({ filters, bounds }: Conjunction): TypedFilter[] => [
  ...filters,
  ...videoLengthTestsOf(bounds),
];

const closedOf = (conjunction: Conjunction): TypedFilter[] => {
  const closed = closedFiltersOf(conjunction);
  return closed.length > 1 ? [{ kind: "and", children: closed }] : closed;
};

const testOf = ({
  leaf,
  member,
  cube,
}: {
  leaf: Leaf;
  member: CubeMember;
  cube: CubeView;
}): Test => {
  const { meta } = member;
  switch (meta.kind) {
    case "tag": {
      return tagTestOf({ leaf, member, column: meta.tagColumn });
    }
    case "measure":
    case "value-list": {
      const field = fieldOf({ member: member.name, cube });
      return valueTestOf({ leaf, member, field, role: "measure" });
    }
    case "creative-media":
    case "nested": {
      // Nested results cannot be filtered as scalar measures.
      throw new Error(`${member.name} cannot be filtered`);
    }
    case "video-length": {
      // Unreachable: the filter typer types a video-length leaf as a bound.
      throw new Error(`${member.name} is typed as a video length bound`);
    }
    case "listed":
    case "free-text":
    case "queried":
    case "number":
    case "time": {
      const field = fieldOf({ member: member.name, cube });
      return valueTestOf({ leaf, member, field, role: "dimension" });
    }
    default: {
      return meta satisfies never;
    }
  }
};

const filterTyper = (cube: CubeView): ((filter: Filter) => Conjunction) => {
  const members = new Map<string, CubeMember>(
    [...cube.measures, ...cube.dimensions].map((member) => [
      member.name,
      member,
    ]),
  );
  const leafOf = (leaf: Leaf): Conjunction => {
    const named = leafMemberOf(leaf);
    const member =
      named.kind === "member" ? members.get(named.name) : undefined;
    if (member === undefined) {
      throw new Error(
        "The query schema names a member of the view on every filter",
      );
    }
    return member.meta.kind === "video-length"
      ? {
          filters: [],
          bounds: [
            {
              dimension: nameInModelOf({ member: member.name, cube }),
              bound: videoLengthBoundOf({ leaf, member }),
            },
          ],
        }
      : { filters: [testOf({ leaf, member, cube })], bounds: [] };
  };
  const andOf = (children: Filter[]): Conjunction => {
    const { filters, bounds } = conjunctionOfAll(children.map(conjunctionOf));
    return {
      filters:
        filters.length === 0 ? [] : [{ kind: "and", children: [...filters] }],
      bounds,
    };
  };
  const orOf = (children: Filter[]): Conjunction => {
    const kept = children.flatMap((child) => closedOf(conjunctionOf(child)));
    return {
      filters: kept.length === 0 ? [] : [{ kind: "or", children: kept }],
      bounds: [],
    };
  };
  const conjunctionOf = (filter: Filter): Conjunction => {
    if ("and" in filter) {
      return andOf(filter.and);
    }
    return "or" in filter ? orOf(filter.or) : leafOf(filter);
  };
  return conjunctionOf;
};

const roleIn =
  (measures: ReadonlySet<string>) =>
  (filter: Filter): FilterRole | "mixed" => {
    const roles = new Set(
      flattenFilters([filter]).map(
        ({ member }): FilterRole =>
          measures.has(member ?? "") ? "having" : "where",
      ),
    );
    const [role, ...others] = [...roles];
    return others.length > 0 ? "mixed" : (role ?? "where");
  };

/** Each top-level filter tests rows or aggregates, as Cube's where and having split them; one that tests both cannot run. */
export const roledFiltersOf = ({
  rows,
  cube,
}: {
  rows: readonly Filter[];
  cube: CubeView;
}): Result<RoledFilter[], "mixes-row-and-aggregate-filters"> => {
  const roleOf = roleIn(new Set(cube.measures.map((measure) => measure.name)));
  const unmixed = rows.flatMap((filter) => {
    const role = roleOf(filter);
    return role === "mixed" ? [] : [{ filter, role }];
  });
  if (unmixed.length < rows.length) {
    return err("mixes-row-and-aggregate-filters");
  }
  const conjunctionOf = filterTyper(cube);
  const filtersOfRole = (role: FilterRole): RoledFilter[] =>
    closedFiltersOf(
      conjunctionOfAll(
        unmixed.flatMap((roled) =>
          roled.role === role ? [conjunctionOf(roled.filter)] : [],
        ),
      ),
    ).map((typed) => ({ role, typed }));
  return ok([...filtersOfRole("where"), ...filtersOfRole("having")]);
};

/** A filter of one view that the Malloy stage can run as it stands. */
export const runnableFilterSchema = (cube: CubeView): z.ZodType<Filter> =>
  cubeFilterSchema(cube).superRefine((filter, ctx) => {
    const roled = roledFiltersOf({ rows: [filter], cube });
    if (isErr(roled)) {
      ctx.addIssue({ code: "custom", message: roled.error });
    }
  });
